import type { BaseSyntheticEvent, MouseEvent } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import type {
  FontValue,
  TextStyleValue
} from '@elfsight/configurator-contracts';
import type {
  FormField,
  FormFieldLayoutProps,
  FormProps,
  FormThemeValue,
  SubmitMessageProps
} from '@elfsight/widget-form-builder/widget';
import {
  Recaptcha,
  SubmitMessage,
  useFormDispatch,
  useFormSelector,
  useWidgetSelector
} from '@elfsight/widget-form-builder/widget';
import type { ButtonProps } from '@elfsight/widget-ui';
import { useBanner } from '../../bannerProvider';
import type { BlockProps } from '../_block';
import { Block } from '../_block';
import { formSettings as defaultFormSettings } from './formSettings';
import { Layout } from './layout';

const ERROR_RECAPTCHA =
  'An error occurred when submitting the form, please try again';
const ERROR_INVALID = 'Please fill all the fields correctly';
const ERROR_REQUIRED = 'Please fill all the required fields';

export type FormLayoutProps = FormProps &
  FormFieldLayoutProps &
  SubmitMessageProps &
  BlockProps & {
    fields: FormField[];
    displayedFields: FormField[];
    theme: FormThemeValue;
    submitButtonProps: ButtonProps;
    font?: FontValue;
    submitButtonText?: string;
    submitButtonAlignment?: 'left' | 'center' | 'right';
    recaptcha?: boolean;
    widgetFooterTextColor?: string;
    widgetFooterLinksColor?: string;
    widgetFooterTextStyle?: TextStyleValue;
    onSubmit(e: BaseSyntheticEvent): Promise<string | undefined>;
  };

export const FormLayout = forwardRef<HTMLDivElement, FormLayoutProps>(
  (
    {
      alignment,
      style,
      fields,
      displayedFields,
      submitButtonProps,
      submitButtonAlignment,
      submitButtonText = defaultFormSettings.submitButtonText,
      theme,
      font,
      fieldSpacing = defaultFormSettings.fieldSpacing,
      submitAction,
      submitMessageTitle,
      submitMessageText,
      submitMessageAlignment,
      submitMessageTitleColor,
      submitMessageTextColor,
      submitMessageTitleFontStyle,
      submitMessageTextFontStyle,
      submitMessageButton,
      submitMessageButtonText,
      submitMessageButtonColor,
      widgetFooterTextColor,
      widgetFooterLinksColor,
      widgetFooterTextStyle,
      recaptcha = false,
      onSubmit,
      onSubmitMessageButtonClick,
      ...forwardingProps
    },
    ref
  ) => {
    const [error, setError] = useState<string | undefined>(undefined);

    const { submitted, closed } = useWidgetSelector();
    const {
      status,
      fieldsError,
      fields: derivedFields,
      started
    } = useFormSelector();
    const dispatch = useFormDispatch();
    const { contentDirection, trackGoogleAnalyticsEvents } = useBanner();

    const isHorizontal = contentDirection === 'row';

    const formSettings = {
      ...forwardingProps,
      fieldSpacing,
      fields: displayedFields,
      submitButtonProps: {
        ...submitButtonProps,
        text: submitButtonText,
        fontFamily: font
      }
    };

    const handleFormChange = () => {
      if (started.value) {
        return;
      }

      trackGoogleAnalyticsEvents?.({ eventType: 'formStarted' });

      dispatch({
        type: 'setStarted',
        started: true
      });
    };

    const handleSubmit = async (e: BaseSyntheticEvent) => {
      const submitError = await onSubmit(e);

      if (submitError && !error) {
        setError(submitError);
      }
    };

    useEffect(() => {
      const invalid = displayedFields.some((field: FormField) => {
        const item = derivedFields[field.id];
        return item?.valid === false && item?.value;
      });

      if (invalid !== fieldsError.invalid) {
        dispatch({
          type: 'setError',
          fieldsError: { invalid },
          status: undefined
        });
      }
    }, [displayedFields]);

    useEffect(() => {
      const required = displayedFields.some((field: FormField) => {
        const { required, value } = derivedFields[field.id] || {};

        const isNonEmpty =
          (typeof value === 'string' || Array.isArray(value)) &&
          value.length > 0;

        return required && !isNonEmpty;
      });
      if (required !== fieldsError.required) {
        dispatch({
          type: 'setError',
          fieldsError: { required },
          status: undefined
        });
      }
    }, [derivedFields, displayedFields, fieldsError, dispatch]);

    useEffect(() => {
      if (status === 'failed') {
        setError(ERROR_RECAPTCHA);
        return;
      }

      if (fieldsError.required) {
        setError(ERROR_REQUIRED);
        return;
      }

      if (fieldsError.invalid) {
        setError(ERROR_INVALID);
        return;
      }

      setError(undefined);
    }, [status, fieldsError]);

    if (closed) {
      return null;
    }

    return (
      <Block
        ref={ref}
        alignment={alignment}
        style={{
          flexDirection: 'column',
          cursor: 'auto',
          fontFamily: font?.value,
          ...style
        }}
        onClick={(e: MouseEvent) => e.stopPropagation()}
      >
        {submitAction === 'message' && submitted && status === 'success' ? (
          <SubmitMessage
            fields={fields}
            isHorizontal={isHorizontal}
            submitMessageButtonColor={submitMessageButtonColor}
            submitMessageTitle={submitMessageTitle}
            submitMessageText={submitMessageText}
            submitMessageAlignment={submitMessageAlignment}
            submitMessageTitleColor={submitMessageTitleColor}
            submitMessageTextColor={submitMessageTextColor}
            submitMessageTitleFontStyle={submitMessageTitleFontStyle}
            submitMessageTextFontStyle={submitMessageTextFontStyle}
            submitMessageButton={submitMessageButton}
            submitMessageButtonText={submitMessageButtonText}
            onSubmitMessageButtonClick={onSubmitMessageButtonClick}
          />
        ) : (
          <>
            <Layout
              isHorizontal={isHorizontal}
              formSettings={formSettings}
              theme={theme}
              handleSubmit={handleSubmit}
              submitButtonAlignment={submitButtonAlignment}
              alignment={alignment}
              contentDirection={contentDirection}
              status={status}
              error={error}
              handleFormChange={handleFormChange}
            />

            {recaptcha && (
              <Recaptcha
                _spacing={fieldSpacing}
                _textColor={widgetFooterTextColor}
                _linksColor={widgetFooterLinksColor}
                _fontSize={widgetFooterTextStyle?.fontSize}
              />
            )}
          </>
        )}
      </Block>
    );
  }
);
